import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { environment } from '../../../environments/environment';
import { OrganizationType, ORGANIZATION_TYPE_OPTIONS, getOrganizationTypeLabel } from 'src/app/core/models/organization.model';

interface Organization {
  id: string;
  name: string;
  organizationType: OrganizationType;
  businessType: string;
  ownerProfileId: string;
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email?: string;
  phone?: string;
  parentOrganizationId?: string;
  billingResponsibility: string;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

interface PaginatedResponse {
  success: boolean;
  data: {
    organizations: Organization[];
    total: number;
  };
}

@Component({
  selector: 'app-organizations',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, PhoneFormatPipe],
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss']
})
export class OrganizationsComponent implements OnInit {
  organizations: Organization[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalOrganizations = 0;
  totalPages = 0;

  // Search & Filters
  searchQuery = '';
  filterType = '';
  filterBusinessType = '';
  sortBy = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Archive filter
  showArchived = false;

  // Organization types for filter dropdown
  organizationTypes: { value: OrganizationType | ''; label: string }[] = [
    { value: '', label: 'All Types' },
    ...ORGANIZATION_TYPE_OPTIONS,
  ];

  businessTypes = [
    { value: '', label: 'All Types' },
    { value: 'Drone Service Provider', label: 'Drone Service Provider' },
    { value: 'Farmer', label: 'Farmer' }
  ];
  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Check for success message from navigation state (e.g., after archiving)
    const navState = history.state;
    if (navState?.successMessage) {
      this.successMessage = navState.successMessage;
    }
    this.loadOrganizations();
  }

  async loadOrganizations(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const params = new URLSearchParams({
        page: this.currentPage.toString(),
        pageSize: this.pageSize.toString(),
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });

      if (this.searchQuery.trim()) {
        params.append('search', this.searchQuery.trim());
      }

      if (this.filterType) {
        params.append('organizationType', this.filterType);
      }

      if (this.filterBusinessType) {
        params.append('businessType', this.filterBusinessType);
      }

      if (this.showArchived) {
        params.append('includeArchived', 'true');
      }

      const response = await firstValueFrom(
        this.http.get<PaginatedResponse>(
          `${environment.apiUrl}/admin/organizations?${params.toString()}`
        )
      );

      if (response.success && response.data) {
        this.organizations = response.data.organizations;
        this.totalOrganizations = response.data.total;
        this.totalPages = Math.ceil(this.totalOrganizations / this.pageSize);
      }
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
      this.errorMessage = 'Failed to load organizations. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadOrganizations();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadOrganizations();
  }

  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterType || this.filterBusinessType || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc' || this.showArchived);
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterType = '';
    this.filterBusinessType = '';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.showArchived = false;
    this.onFilterChange();
  }

  onShowArchivedChange(): void {
    this.currentPage = 1;
    this.loadOrganizations();
  }

  onSortChange(column: string): void {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.loadOrganizations();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadOrganizations();
    }
  }

  viewOrganization(org: Organization): void {
    this.router.navigate(['/organizations', org.id]);
  }

  getOrganizationTypeLabel = getOrganizationTypeLabel;

  getBusinessTypeLabel(type: string): string {
    // Treat a falsy businessType as "None" before the lookup — otherwise the
    // filter dropdown's empty-value entry ({ value: '', label: 'All Types' })
    // would make a missing business type render as "All Types".
    if (!type) {
      return 'None';
    }
    const found = this.businessTypes.find(t => t.value === type);
    return found?.label || type;
  }

  getOrganizationTypeClass(type: OrganizationType): string {
    switch (type) {
      case OrganizationType.Llc:
      case OrganizationType.SCorp:
      case OrganizationType.CCorp:
        return 'bg-primary';
      case OrganizationType.Partnership:
        return 'bg-info';
      case OrganizationType.SoleProprietorship:
      case OrganizationType.Individual:
        return 'bg-secondary';
      case OrganizationType.NonProfit:
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  }

  getBusinessTypeClass(type: string): string {
    // A missing business type is a neutral "None", not an error state.
    if (!type) {
      return 'bg-secondary';
    }
    switch (type) {
      case 'Drone Service Provider':
        return 'bg-info';
      case 'Farmer':
        return 'bg-success';
      default:
        return 'bg-danger';
    }
  }

  formatAddress(org: Organization): string {
    const parts = [org.city, org.state].filter(Boolean);
    return parts.join(', ');
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalOrganizations);
  }
}
